import { useEffect } from 'react';

export interface SEOProps {
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  ogUrl?: string;
  ogType?: string;
  ogSiteName?: string;
  ogLocale?: string;
  twitterCard?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleSection?: string;
  articleAuthor?: string;
  jsonLdSchemas?: Record<string, unknown>[];
}

export default function SEO({
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType,
  ogSiteName,
  ogLocale,
  twitterCard,
  articlePublishedTime,
  articleModifiedTime,
  articleSection,
  articleAuthor,
  jsonLdSchemas,
}: SEOProps) {
  // index.html trae un <link rel="canonical"> apuntando a la home. Al navegar
  // dentro de la SPA hay que reapuntarlo, no añadir otro: dos canonicals hacen
  // que Google descarte ambos y elija la URL por su cuenta.
  useEffect(() => {
    if (!ogUrl) return;
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = ogUrl;
  }, [ogUrl]);

  // JSON-LD structured data via useEffect (Google can execute JS)
  useEffect(() => {
    if (!jsonLdSchemas || jsonLdSchemas.length === 0) return;

    const existingScripts = document.querySelectorAll('script[id^="seo-jsonld-"]');
    existingScripts.forEach((s) => s.remove());

    const scripts: HTMLScriptElement[] = [];
    jsonLdSchemas.forEach((schema, index) => {
      const id = `seo-jsonld-${index}`;
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      script.textContent = JSON.stringify({ '@context': 'https://schema.org', ...schema });
      document.head.appendChild(script);
      scripts.push(script);
    });

    return () => {
      scripts.forEach((s) => {
        const existing = document.getElementById(s.id);
        if (existing) existing.remove();
      });
    };
  }, [JSON.stringify(jsonLdSchemas)]);

  return (
    <>
      {ogTitle && <title>{ogTitle}</title>}
      {ogTitle && <meta property="og:title" content={ogTitle} />}
      {ogDescription && <meta property="og:description" content={ogDescription} />}
      {ogDescription && <meta name="description" content={ogDescription} />}
      {ogImage && <meta property="og:image" content={ogImage} />}
      {ogImage && <meta property="og:image:secure_url" content={ogImage} />}
      {ogImage && <meta property="og:image:width" content="1200" />}
      {ogImage && <meta property="og:image:height" content="630" />}
      {ogImage && <meta property="og:image:type" content="image/jpeg" />}
      {ogImage && <meta property="og:image:alt" content={ogTitle || ''} />}
      {ogUrl && <meta property="og:url" content={ogUrl} />}
      {ogType && <meta property="og:type" content={ogType} />}
      {ogSiteName && <meta property="og:site_name" content={ogSiteName} />}
      {ogLocale && <meta property="og:locale" content={ogLocale} />}
      {articlePublishedTime && <meta property="article:published_time" content={articlePublishedTime} />}
      {articleModifiedTime && <meta property="article:modified_time" content={articleModifiedTime} />}
      {articleSection && <meta property="article:section" content={articleSection} />}
      {articleAuthor && <meta property="article:author" content={articleAuthor} />}
      {twitterCard && <meta name="twitter:card" content={twitterCard} />}
      {ogTitle && <meta name="twitter:title" content={ogTitle} />}
      {ogDescription && <meta name="twitter:description" content={ogDescription} />}
      {ogImage && <meta name="twitter:image" content={ogImage} />}
    </>
  );
}